import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { wyloguj } from '../utils/wyloguj';
import { AlertIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import './SuperAdmin.css';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import { useConfirm } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { INVENTORY_PRESETS } from '../utils/inventoryPresets';
import DemoManageModal from '../components/superadmin/DemoManageModal';
import PauseManageModal, { stanPrzerwyFirmy } from '../components/superadmin/PauseManageModal';
import ResignManageModal from '../components/superadmin/ResignManageModal';
import DeleteResignedDataModal from '../components/superadmin/DeleteResignedDataModal';
import { stanRezygnacjiFirmy, usuniecieDanychDostepne } from '../utils/rezygnacja';
import { formatDate, STREFA_FIRMY } from '../utils/format';

// SVG Ikony
const BuildingIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22V12h6v10"/><path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const CrownIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h20"/><path d="M4 17l2-12 4 5 2-7 2 7 4-5 2 12"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

/**
 * „Usuń dane firmy” w wierszu firmy po rezygnacji (bez demo). Działa dopiero od dnia usunięcia
 * (dzień rezygnacji + 30, `resignation_delete_from` z listy); wcześniej obok stoi dzień, od którego
 * będzie dostępny, jako zwykły tekst, bo dymka `title` nie widać dotykiem. Warunki sprawdza serwer.
 */
function PrzyciskUsunieciaDanych({ org, onOpen }) {
  const { t, currentLanguage } = useTranslation();
  const dost = usuniecieDanychDostepne(org);
  if (!dost.widoczne) return null;
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        className="btn-small btn-danger"
        onClick={onOpen}
        disabled={!dost.dostepne}
        title={t('superAdmin.deleteDataTooltip')}
        data-cy="delete-data-open"
      >
        {t('superAdmin.deleteData')}
      </button>
      {!dost.dostepne && dost.od && (
        <small style={{ color: 'var(--text-muted)', fontSize: '12px' }} data-cy="delete-data-available-from">
          {t('superAdmin.deleteDataFrom', { od: formatDate(dost.od, { locale: currentLanguage }) })}
        </small>
      )}
    </span>
  );
}

/**
 * Panel Super-Admina
 * Zarządzanie organizacjami w systemie Multi-tenancy
 */
function SuperAdmin() {
  const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // FAZA T, T.3: serwer odmawia panelu bez 2FA. Dwa przypadki, dwie różne instrukcje:
  // `2fa_required` = konto nie ma 2FA (włącz je), `2fa_session_required` = 2FA jest, ale ta sesja
  // weszła bez kodu (wyloguj się i zaloguj z kodem). null = panel dostępny.
  const [wymaga2fa, setWymaga2fa] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateDemoModal, setShowCreateDemoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, active, inactive

  const [newOrg, setNewOrg] = useState({
    name: '',
    tax_id: '',
    email: '',
    phone: '',
    address: '',
    subscription_plan: 'basic'
  });

  const [editOrg, setEditOrg] = useState(null);

  const [newDemoOrg, setNewDemoOrg] = useState({ ownerEmail: '', ownerName: '', preset: 'ski', days: '14' });
  const [managedDemoOrg, setManagedDemoOrg] = useState(null);
  // F.11: okno przerwy w sezonie dla wybranej firmy.
  const [managedPauseOrg, setManagedPauseOrg] = useState(null);
  const [managedResignOrg, setManagedResignOrg] = useState(null);
  // Ręczne usunięcie danych firmy po rezygnacji (od dnia usunięcia, okno z potwierdzeniem nazwą).
  const [deleteDataOrg, setDeleteDataOrg] = useState(null);

  const [createdLink, setCreatedLink] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null); // null = nie sprawdzane, { configured, verified, error }
  const [resendingOrgId, setResendingOrgId] = useState(null);

  useEffect(() => {
    loadOrganizations();
  }, [filterStatus]);

  // Sprawdź status SMTP przy otwarciu modala tworzenia (org zwykła lub demo)
  useEffect(() => {
    if ((showCreateModal || showCreateDemoModal) && smtpStatus === null) {
      fetch(`${API_BASE}/api/super-admin/smtp-status`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => setSmtpStatus(data))
        .catch(() => setSmtpStatus({ configured: false, verified: false, error: t('superAdmin.smtpCheckFailed') }));
    }
  }, [showCreateModal, showCreateDemoModal, smtpStatus]);

  const loadOrganizations = async () => {
    setLoading(true);
    setError('');

    try {
      let url = `${API_BASE}/api/super-admin/organizations`;
      const params = new URLSearchParams();

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.status === 403) {
        const odp = await response.json().catch(() => ({}));
        if (odp.code === '2fa_required' || odp.code === '2fa_session_required') {
          setWymaga2fa(odp.code);
          return;
        }
      }
      if (!response.ok) {
        throw new Error(t('superAdmin.loadError'));
      }
      setWymaga2fa(null);

      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadOrganizations();
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();

    if (!newOrg.name || !newOrg.email) {
      toast.error(t('superAdmin.nameEmailRequired'));
      return;
    }

    try {
      const csrfToken = getCookie('csrf');

      const response = await fetch(`${API_BASE}/api/super-admin/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify(newOrg)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCreatedLink({
          url: data.activationUrl,
          token: data.token,
          organizationName: newOrg.name,
          emailSent: data.emailSent,
          emailError: data.emailError,
          recipientEmail: newOrg.email
        });
        setNewOrg({
          name: '',
          tax_id: '',
          email: '',
          phone: '',
          address: '',
          subscription_plan: 'basic'
        });
        loadOrganizations();
      } else {
        toast.error(data.error || t('superAdmin.createError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    }
  };

  const handleCreateDemoOrg = async (e) => {
    e.preventDefault();

    if (!newDemoOrg.ownerEmail || !newDemoOrg.ownerName) {
      toast.error(t('superAdmin.ownerRequired'));
      return;
    }

    try {
      const csrfToken = getCookie('csrf');

      const response = await fetch(`${API_BASE}/api/super-admin/organizations/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify(newDemoOrg)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCreatedLink({
          url: data.activationUrl,
          organizationName: data.organizationName || `Demo: ${newDemoOrg.ownerName}`,
          emailSent: data.emailSent,
          emailError: data.emailError,
          recipientEmail: newDemoOrg.ownerEmail
        });
        setNewDemoOrg({ ownerEmail: '', ownerName: '', preset: 'ski', days: '14' });
        setShowCreateDemoModal(false);
        loadOrganizations();
      } else {
        toast.error(data.error || t('superAdmin.createDemoError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    }
  };

  const handleToggleActive = async (orgId, currentStatus) => {
    // Nie pozwalaj na zmianę statusu dla organizacji oczekujących na aktywację
    if (currentStatus === 'pending') {
      toast.warning(t('superAdmin.pendingStatusLocked'));
      return;
    }

    // Firma w przerwie (F.11) i po rezygnacji (F.25) wyłącza się jak aktywna; serwer kończy wtedy
    // przerwę albo odwołuje rezygnację (firma nieaktywna nie ma terminu usunięcia danych).
    const newStatus = ['active', 'paused', 'resigned'].includes(currentStatus) ? 'inactive' : 'active';
    let confirmMessage = newStatus === 'active'
      ? t('superAdmin.confirmActivate')
      : t('superAdmin.confirmDeactivate');
    if (currentStatus === 'resigned') confirmMessage = t('superAdmin.confirmDeactivateResigned');

    const ok = await confirm({ title: confirmMessage, tone: newStatus === 'active' ? 'primary' : 'danger' });
    if (!ok) {
      return;
    }

    try {
      const csrfToken = getCookie('csrf');

      const response = await fetch(`${API_BASE}/api/super-admin/organizations/${orgId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          status: newStatus
        })
      });

      const data = await response.json();

      if (response.ok) {
        loadOrganizations();
      } else {
        toast.error(data.error || t('superAdmin.updateError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    }
  };

  const handleEditOrganization = (org) => {
    setEditOrg({
      id: org.id,
      name: org.name,
      tax_id: org.tax_id || '',
      email: org.email,
      phone: org.phone || '',
      address: org.address || '',
      subscription_plan: org.subscription_plan
    });
    setShowEditModal(true);
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();

    if (!editOrg.name || !editOrg.email) {
      toast.error(t('superAdmin.nameEmailRequired'));
      return;
    }

    try {
      const csrfToken = getCookie('csrf');

      const response = await fetch(`${API_BASE}/api/super-admin/organizations/${editOrg.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          name: editOrg.name,
          tax_id: editOrg.tax_id,
          email: editOrg.email,
          phone: editOrg.phone,
          address: editOrg.address,
          subscription_plan: editOrg.subscription_plan
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowEditModal(false);
        setEditOrg(null);
        loadOrganizations();
        toast.success(t('superAdmin.orgUpdated'));
      } else {
        toast.error(data.error || t('superAdmin.updateError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    }
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('superAdmin.copied'));
  };

  // Liczba dni do wygaśnięcia dostępu demo (access_expires_at ustawiane dopiero po aktywacji — D.3)
  const getDemoDaysLabel = (org) => {
    // Data automatycznego usunięcia (backend: demo_purge_at, modules/demoLifecycle.js) — pokazywana
    // przy demo nieaktywowanym i wygasłym, bo tylko te dwa stany do niego prowadzą.
    const purge = org.demo_purge_at
      ? ` · ${t('superAdmin.demoPurgeOn', { date: new Date(org.demo_purge_at).toLocaleDateString('pl-PL') })}`
      : '';
    if (!org.access_expires_at) return t('superAdmin.demoAwaiting') + purge;
    const msLeft = new Date(org.access_expires_at).getTime() - Date.now();
    if (msLeft <= 0) return t('superAdmin.demoExpired') + purge;
    const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
    return `${daysLeft} ${daysLeft === 1 ? t('superAdmin.dayOne') : t('superAdmin.dayMany')}`;
  };

  // Wysyła ponownie email aktywacyjny do organizacji pending
  const handleResendActivation = async (org) => {
    const ok = await confirm({
      title: t('superAdmin.confirmResend').replace('{name}', org.name).replace('{email}', org.email),
    });
    if (!ok) return;

    setResendingOrgId(org.id);
    try {
      const csrfToken = getCookie('csrf');
      const response = await fetch(`${API_BASE}/api/super-admin/organizations/${org.id}/resend-activation`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok && data.success) {
        // Otwórz modal z linkiem aktywacyjnym (taki sam jak po utworzeniu org)
        setCreatedLink({
          url: data.activationUrl,
          token: data.token,
          organizationName: org.name,
          emailSent: data.emailSent,
          emailError: data.emailError,
          recipientEmail: org.email
        });
      } else {
        toast.error(data.error || t('superAdmin.resendError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    } finally {
      setResendingOrgId(null);
    }
  };

  // Usuwa pending organizację
  const handleDeletePending = async (org) => {
    const ok = await confirm({
      title: t('superAdmin.confirmDeletePending').replace('{name}', org.name),
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const csrfToken = getCookie('csrf');
      const response = await fetch(`${API_BASE}/api/super-admin/organizations/${org.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        loadOrganizations();
      } else {
        toast.error(data.error || t('superAdmin.deleteError'));
      }
    } catch (err) {
      toast.error(t('superAdmin.serverError'));
    }
  };

  if (wymaga2fa) {
    const sesjaBezKodu = wymaga2fa === '2fa_session_required';
    return (
      <div className="super-admin-page">
        <div className="super-admin-header">
          <h1><span className="header-icon"><ShieldIcon /></span> {t('superAdmin.title')}</h1>
        </div>
        <div className="twofa-required" data-cy="superadmin-2fa-required" data-powod={wymaga2fa}>
          <h2>{t(sesjaBezKodu ? 'superAdmin.twoFactorSessionTitle' : 'superAdmin.twoFactorRequiredTitle')}</h2>
          <p>{t(sesjaBezKodu ? 'superAdmin.twoFactorSessionBody' : 'superAdmin.twoFactorRequiredBody')}</p>
          {sesjaBezKodu
            ? <button type="button" className="btn-primary" onClick={wyloguj}>{t('superAdmin.twoFactorSessionCta')}</button>
            : <Link to="/security" className="btn-primary">{t('superAdmin.twoFactorRequiredCta')}</Link>}
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-page">
      <div className="super-admin-header">
        <h1><span className="header-icon"><ShieldIcon /></span> {t('superAdmin.title')}</h1>
        <p>{t('superAdmin.subtitle')}</p>
      </div>

      {/* Karty statystyk */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><BuildingIcon /></div>
          <div className="stat-content">
            <div className="stat-value">{organizations.length}</div>
            <div className="stat-label">{t('superAdmin.statAll')}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><CheckCircleIcon /></div>
          <div className="stat-content">
            <div className="stat-value">{organizations.filter(o => o.status === 'active').length}</div>
            <div className="stat-label">{t('superAdmin.statActive')}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><CrownIcon /></div>
          <div className="stat-content">
            <div className="stat-value">{organizations.filter(o => o.subscription_plan === 'premium').length}</div>
            <div className="stat-label">Premium</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon amber"><ClockIcon /></div>
          <div className="stat-content">
            <div className="stat-value">{organizations.filter(o => o.status === 'pending').length}</div>
            <div className="stat-label">{t('superAdmin.statPending')}</div>
          </div>
        </div>
      </div>

      <div className="super-admin-actions">
        <div className="actions-row-top">
          <button
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + {t('superAdmin.addTitle')}
          </button>

          <button
            className="btn-secondary"
            onClick={() => setShowCreateDemoModal(true)}
          >
            + {t('superAdmin.demoTitle')}
          </button>

          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder={t('superAdmin.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">{t('superAdmin.search')}</button>
          </form>
        </div>

        <div className="filter-buttons">
          <button
            className={filterStatus === 'all' ? 'active' : ''}
            onClick={() => setFilterStatus('all')}
          >
            {t('superAdmin.filterAll')}
          </button>
          <button
            className={filterStatus === 'pending' ? 'active' : ''}
            onClick={() => setFilterStatus('pending')}
          >
            {t('superAdmin.statusSent')}
          </button>
          <button
            className={filterStatus === 'active' ? 'active' : ''}
            onClick={() => setFilterStatus('active')}
          >
            {t('superAdmin.filterActive')}
          </button>
          <button
            className={filterStatus === 'paused' ? 'active' : ''}
            onClick={() => setFilterStatus('paused')}
            data-cy="superadmin-filter-paused"
          >
            {t('superAdmin.filterPaused')}
          </button>
          <button
            className={filterStatus === 'resigned' ? 'active' : ''}
            onClick={() => setFilterStatus('resigned')}
            data-cy="superadmin-filter-resigned"
          >
            {t('superAdmin.filterResigned')}
          </button>
          <button
            className={filterStatus === 'inactive' ? 'active' : ''}
            onClick={() => setFilterStatus('inactive')}
          >
            {t('superAdmin.filterInactive')}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">{t('superAdmin.loading')}</div>
      ) : (
        <div className="organizations-list">
          {organizations.length === 0 ? (
            <div className="empty-state">
              <p>{t('superAdmin.empty')}</p>
            </div>
          ) : (
            <table className="organizations-table">
              <thead>
                <tr>
                  <th>{t('superAdmin.colId')}</th>
                  <th>{t('superAdmin.colName')}</th>
                  <th>{t('superAdmin.colNip')}</th>
                  <th>{t('superAdmin.colEmail')}</th>
                  <th>{t('superAdmin.colPlan')}</th>
                  <th>{t('superAdmin.colStatus')}</th>
                  <th>{t('superAdmin.colDemo')}</th>
                  <th>{t('superAdmin.colCreated')}</th>
                  <th>{t('superAdmin.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map(org => (
                  <tr key={org.id} className={org.status === 'inactive' ? 'inactive' : (org.status === 'pending' ? 'pending' : '')}>
                    <td>{org.id}</td>
                    <td><strong>{org.name}</strong></td>
                    <td>{org.tax_id || '-'}</td>
                    <td>{org.email || '-'}</td>
                    <td>
                      <span className={`badge badge-${org.subscription_plan}`}>
                        {org.subscription_plan}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${org.status}`}>
                        {org.status === 'pending' && t('superAdmin.statusSent')}
                        {org.status === 'active' && `✓ ${t('superAdmin.statusActive')}`}
                        {org.status === 'inactive' && `✗ ${t('superAdmin.statusInactive')}`}
                        {org.status === 'paused' && (stanPrzerwyFirmy(org) === 'trwa'
                          ? t('superAdmin.statusPaused')
                          : t('superAdmin.statusPauseScheduled', { od: formatDate(org.paused_at, { locale: currentLanguage, timeZone: STREFA_FIRMY }) }))}
                        {org.status === 'resigned' && (stanRezygnacjiFirmy(org) === 'trwa'
                          ? t('superAdmin.statusResigned', { od: formatDate(org.resignation_delete_from, { locale: currentLanguage }) })
                          : t('superAdmin.statusResignScheduled', { od: formatDate(org.resigned_at, { locale: currentLanguage, timeZone: STREFA_FIRMY }) }))}
                      </span>
                    </td>
                    <td>
                      {org.is_demo ? (
                        <span className="badge badge-demo" title={getDemoDaysLabel(org)}>
                          DEMO · {getDemoDaysLabel(org)}
                        </span>
                      ) : '-'}
                    </td>
                    <td>{new Date(org.created_at).toLocaleDateString('pl-PL')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleEditOrganization(org)}
                          title={t('superAdmin.editTooltip')}
                        >
                          {t('common.edit')}
                        </button>
                        {org.is_demo && (
                          <button
                            className="btn-small btn-success"
                            onClick={() => setManagedDemoOrg(org)}
                            title={t('superAdmin.demoManageTooltip')}
                            data-cy="demo-manage-open"
                          >
                            {t('superAdmin.demoManage')}
                          </button>
                        )}
                        {!org.is_demo && (org.status === 'active' || org.status === 'paused') && (
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => setManagedPauseOrg(org)}
                            title={t('superAdmin.pauseTooltip')}
                            data-cy="pause-manage-open"
                          >
                            {t('superAdmin.pause')}
                          </button>
                        )}
                        {!org.is_demo && ['active', 'paused', 'inactive', 'resigned'].includes(org.status) && (
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => setManagedResignOrg(org)}
                            title={t('superAdmin.resignTooltip')}
                            data-cy="resign-manage-open"
                          >
                            {t('superAdmin.resign')}
                          </button>
                        )}
                        <PrzyciskUsunieciaDanych org={org} onOpen={() => setDeleteDataOrg(org)} />
                        {org.status === 'pending' ? (
                          <>
                            <button
                              className="btn-small btn-success"
                              onClick={() => handleResendActivation(org)}
                              disabled={resendingOrgId === org.id}
                              title={t('superAdmin.resendTooltip')}
                            >
                              {resendingOrgId === org.id ? t('superAdmin.resending') : t('superAdmin.resend')}
                            </button>
                            <button
                              className="btn-small btn-danger"
                              onClick={() => handleDeletePending(org)}
                              title={t('superAdmin.deletePendingTooltip')}
                            >
                              {t('superAdmin.delete')}
                            </button>
                          </>
                        ) : (
                          <button
                            className={`btn-small ${org.status === 'inactive' ? 'btn-success' : 'btn-danger'}`}
                            onClick={() => handleToggleActive(org.id, org.status)}
                          >
                            {org.status === 'inactive' ? t('superAdmin.activate') : t('superAdmin.deactivate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal tworzenia organizacji */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('superAdmin.addTitle')}</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            {smtpStatus && !smtpStatus.verified && (
              <div style={{
                background: 'var(--warning-bg)',
                border: '1px solid var(--warning-border)',
                borderRadius: '8px',
                padding: '12px 16px',
                margin: '0 24px 16px 24px'
              }}>
                <p style={{ margin: 0, color: 'var(--warning)', fontWeight: 600 }}>
                  <AlertIcon size={16} /> Email serwer nie działa
                </p>
                <p style={{ margin: '6px 0 0 0', color: 'var(--warning)', fontSize: '13px' }}>
                  {t('superAdmin.noteBefore')}<strong>{t('superAdmin.noteBold')}</strong>.{' '}
                  {t('superAdmin.noteCopyClient')}
                </p>
                {smtpStatus.error && (
                  <p style={{ margin: '6px 0 0 0', color: 'var(--warning)', fontSize: '11px' }}>
                    {t('superAdmin.reason')}: {smtpStatus.error}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleCreateOrganization} className="org-form">
              <div className="form-group">
                <label>{t('superAdmin.fieldName')}</label>
                <input
                  type="text"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldEmail')}</label>
                <input
                  type="email"
                  value={newOrg.email}
                  onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                  required
                />
                <small>{t('superAdmin.emailHint')}</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('superAdmin.fieldNip')}</label>
                  <input
                    type="text"
                    value={newOrg.tax_id}
                    onChange={(e) => setNewOrg({ ...newOrg, tax_id: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>{t('superAdmin.fieldPhone')}</label>
                  <input
                    type="tel"
                    value={newOrg.phone}
                    onChange={(e) => setNewOrg({ ...newOrg, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldAddress')}</label>
                <textarea
                  value={newOrg.address}
                  onChange={(e) => setNewOrg({ ...newOrg, address: e.target.value })}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldPlan')}</label>
                <SimpleSelect
                  style={{ width: '100%' }}
                  value={newOrg.subscription_plan}
                  onChange={(val) => setNewOrg({ ...newOrg, subscription_plan: val })}
                  options={[
                    { value: 'basic', label: 'Basic' },
                    { value: 'premium', label: 'Premium' },
                  ]}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('superAdmin.submitCreate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal tworzenia organizacji demo */}
      {showCreateDemoModal && (
        <div className="modal-overlay" onClick={() => setShowCreateDemoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('superAdmin.demoTitle')}</h2>
              <button className="close-btn" onClick={() => setShowCreateDemoModal(false)}>×</button>
            </div>

            {smtpStatus && !smtpStatus.verified && (
              <div style={{
                background: 'var(--warning-bg)',
                border: '1px solid var(--warning-border)',
                borderRadius: '8px',
                padding: '12px 16px',
                margin: '0 24px 16px 24px'
              }}>
                <p style={{ margin: 0, color: 'var(--warning)', fontWeight: 600 }}>
                  <AlertIcon size={16} /> Email serwer nie działa
                </p>
                <p style={{ margin: '6px 0 0 0', color: 'var(--warning)', fontSize: '13px' }}>
                  {t('superAdmin.demoNoteBefore')}<strong>{t('superAdmin.noteBold')}</strong>.{' '}
                  {t('superAdmin.noteCopyLead')}
                </p>
              </div>
            )}

            <form onSubmit={handleCreateDemoOrg} className="org-form">
              <div className="form-group">
                <label>{t('superAdmin.fieldOwnerName')}</label>
                <input
                  type="text"
                  value={newDemoOrg.ownerName}
                  onChange={(e) => setNewDemoOrg({ ...newDemoOrg, ownerName: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldOwnerEmail')}</label>
                <input
                  type="email"
                  value={newDemoOrg.ownerEmail}
                  onChange={(e) => setNewDemoOrg({ ...newDemoOrg, ownerEmail: e.target.value })}
                  required
                />
                <small>{t('superAdmin.demoEmailHint')}</small>
              </div>

              {/* X.11: branża danych demo. Klucze = presety magazynu (utils/inventoryPresets.js),
                  lustrzane po stronie backendu w modules/demoPresets.js. */}
              <div className="form-group">
                <label>{t('superAdmin.demoPresetLabel')}</label>
                <SimpleSelect
                  style={{ width: '100%' }}
                  value={newDemoOrg.preset}
                  onChange={(val) => setNewDemoOrg({ ...newDemoOrg, preset: val })}
                  options={INVENTORY_PRESETS.map((p) => ({ value: p.key, label: t(p.nameKey) }))}
                  data-cy="demo-preset-select"
                />
                <small>{t('superAdmin.demoPresetHint')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="demo-days">{t('superAdmin.demoDaysLabel')}</label>
                <input
                  id="demo-days"
                  type="number"
                  min="1"
                  max="365"
                  value={newDemoOrg.days}
                  onChange={(e) => setNewDemoOrg({ ...newDemoOrg, days: e.target.value })}
                  data-cy="demo-days"
                />
                <small>{t('superAdmin.demoDaysHint')}</small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateDemoModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('superAdmin.submitCreateDemo')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {managedPauseOrg && (
        <PauseManageModal
          org={managedPauseOrg}
          onClose={() => setManagedPauseOrg(null)}
          onChanged={() => { setManagedPauseOrg(null); loadOrganizations(); }}
        />
      )}

      {managedResignOrg && (
        <ResignManageModal
          org={managedResignOrg}
          onClose={() => setManagedResignOrg(null)}
          onChanged={() => { setManagedResignOrg(null); loadOrganizations(); }}
        />
      )}

      {deleteDataOrg && (
        <DeleteResignedDataModal
          org={deleteDataOrg}
          onClose={() => setDeleteDataOrg(null)}
          onDeleted={() => { setDeleteDataOrg(null); loadOrganizations(); }}
          onStale={loadOrganizations}
        />
      )}

      {managedDemoOrg && (
        <DemoManageModal
          org={managedDemoOrg}
          onClose={() => setManagedDemoOrg(null)}
          onChanged={() => { setManagedDemoOrg(null); loadOrganizations(); }}
        />
      )}

      {/* Modal z linkiem aktywacyjnym */}
      {createdLink && (
        <div className="modal-overlay" onClick={() => setCreatedLink(null)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✓ {t('superAdmin.createdTitle')}</h2>
              <button className="close-btn" onClick={() => setCreatedLink(null)}>×</button>
            </div>

            <div className="modal-body">
              <p>{t('superAdmin.createdBefore')}<strong>{createdLink.organizationName}</strong>{t('superAdmin.createdAfter')}</p>

              {createdLink.emailSent ? (
                <p style={{ color: 'var(--success)' }}>
                  {t('superAdmin.sentTo')} <strong>{createdLink.recipientEmail}</strong>.
                </p>
              ) : (
                <div style={{
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: 0, color: 'var(--warning)', fontWeight: 600 }}>
                    <AlertIcon size={16} /> {t('superAdmin.emailNotSent')}
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--warning)' }}>
                    {t('superAdmin.copyLinkManually')}
                  </p>
                  {createdLink.emailError && (
                    <p style={{ margin: '8px 0 0 0', color: 'var(--warning)', fontSize: '12px' }}>
                      {t('superAdmin.reason')}: {createdLink.emailError}
                    </p>
                  )}
                </div>
              )}

              <div className="activation-link-box">
                <label>{t('superAdmin.activationLink')}</label>
                <div className="link-display">
                  <input type="text" value={createdLink.url} readOnly />
                  <button onClick={() => copyToClipboard(createdLink.url)}>
                    {t('superAdmin.copy')}
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-primary" onClick={() => setCreatedLink(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal edycji organizacji */}
      {showEditModal && editOrg && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('superAdmin.editTitle')}</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>

            <form onSubmit={handleUpdateOrganization} className="org-form">
              <div className="form-group">
                <label>{t('superAdmin.fieldName')}</label>
                <input
                  type="text"
                  value={editOrg.name}
                  onChange={(e) => setEditOrg({ ...editOrg, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldEmail')}</label>
                <input
                  type="email"
                  value={editOrg.email}
                  onChange={(e) => setEditOrg({ ...editOrg, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('superAdmin.fieldNip')}</label>
                  <input
                    type="text"
                    value={editOrg.tax_id}
                    onChange={(e) => setEditOrg({ ...editOrg, tax_id: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>{t('superAdmin.fieldPhone')}</label>
                  <input
                    type="tel"
                    value={editOrg.phone}
                    onChange={(e) => setEditOrg({ ...editOrg, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldAddress')}</label>
                <textarea
                  value={editOrg.address}
                  onChange={(e) => setEditOrg({ ...editOrg, address: e.target.value })}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.fieldPlan')}</label>
                <SimpleSelect
                  style={{ width: '100%' }}
                  value={editOrg.subscription_plan}
                  onChange={(val) => setEditOrg({ ...editOrg, subscription_plan: val })}
                  options={[
                    { value: 'basic', label: 'Basic' },
                    { value: 'premium', label: 'Premium' },
                  ]}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('superAdmin.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdmin;
